/**
 * Preferences and account.
 *
 * Appearance is local to the browser; the password change is not. Since the renewal-notice
 * email arrived, neither is the whole bottom half of this screen: the template and its dispatch
 * mode are agency-wide settings, stored server-side and shown to admins only — which is why the
 * card announces whose settings they are rather than sitting silently among the personal ones.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { Check, LogOut, Mail, Monitor, Moon, Send, Sun, TriangleAlert } from 'lucide-react';
import { changePasswordSchema, renewalEmailSettingsSchema, type RenewalEmailMode } from '@ias/shared';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, Separator, Skeleton, Switch } from '@/components/ui/misc';
import { EnumBadge } from '@/components/common/enum-badge';
import { PageHeader } from '@/components/common/page-header';
import { RequiresConnection } from '@/components/offline/offline-indicator';
import { ApiRequestError, post } from '@/lib/api/client';
import {
  useRenewalEmailSettings,
  useSaveRenewalEmailSettings,
  useSendTestRenewalEmail,
} from '@/lib/api/domains';
import { useAuth } from '@/lib/auth';
import { LOCALES, useI18n } from '@/lib/i18n';
import { usePreferences, type Density, type ThemeMode } from '@/lib/preferences';
import { cn, initials } from '@/lib/utils';

const LOCALE_NAMES: Record<string, string> = { el: 'Ελληνικά', en: 'English' };

export function SettingsPage() {
  const { t, locale, setLocale } = useI18n();
  const { user, signOut, can } = useAuth();
  const { theme, setTheme, density, setDensity } = usePreferences();

  return (
    <>
      <PageHeader title={t('settings.title')} />

      <div className="grid max-w-3xl gap-4">
        <Card>
          <CardHeader>
            <div>
              <CardTitle>{t('auth.profile')}</CardTitle>
              <CardDescription>{user?.email}</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-4">
            <Avatar className="size-14">
              <AvatarFallback className="text-lg">{initials(user?.fullName ?? '')}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{user?.fullName}</p>
              <div className="mt-1">
                <EnumBadge enum="role" value={user?.role} size="sm" />
              </div>
            </div>
            <Button variant="outline" onClick={() => void signOut()}>
              <LogOut /> {t('auth.signOut')}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>{t('settings.appearance')}</CardTitle>
              <CardDescription>{t('settings.theme')}</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <OptionRow label={t('settings.theme')}>
              {(
                [
                  { value: 'light', label: t('settings.themeLight'), icon: Sun },
                  { value: 'dark', label: t('settings.themeDark'), icon: Moon },
                  { value: 'system', label: t('settings.themeSystem'), icon: Monitor },
                ] as const
              ).map((option) => (
                <SegmentButton
                  key={option.value}
                  active={theme === option.value}
                  onClick={() => setTheme(option.value as ThemeMode)}
                >
                  <option.icon className="size-3.5" />
                  {option.label}
                </SegmentButton>
              ))}
            </OptionRow>

            <Separator />

            <OptionRow label={t('settings.language')}>
              {LOCALES.map((code) => (
                <SegmentButton key={code} active={locale === code} onClick={() => setLocale(code)}>
                  {LOCALE_NAMES[code] ?? code}
                </SegmentButton>
              ))}
            </OptionRow>

            <Separator />

            <OptionRow label={t('settings.density')}>
              {(
                [
                  { value: 'comfortable', label: t('settings.densityComfortable') },
                  { value: 'compact', label: t('settings.densityCompact') },
                ] as const
              ).map((option) => (
                <SegmentButton
                  key={option.value}
                  active={density === option.value}
                  onClick={() => setDensity(option.value as Density)}
                >
                  {option.label}
                </SegmentButton>
              ))}
            </OptionRow>
          </CardContent>
        </Card>

        <PasswordCard />

        {/* Agency-wide, and the one setting here that makes the system write to people outside
            the office — so it is an admin's to change, and it is stated as such. */}
        {can('user:manage') && <RenewalEmailCard />}
      </div>
    </>
  );
}

function OptionRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <Label className="text-sm">{label}</Label>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function SegmentButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[13px] font-medium transition-colors',
        active
          ? 'border-primary bg-[var(--tint-info-bg)] text-primary'
          : 'hover:bg-accent text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
      {active && <Check className="size-3" />}
    </button>
  );
}

function PasswordCard() {
  const { t } = useI18n();
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const parsed = changePasswordSchema.safeParse({ currentPassword, newPassword });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('error.generic'));
      return;
    }

    setSaving(true);
    try {
      await post('/auth/change-password', parsed.data);
      // The API revokes every session on a password change, so the user is signed out.
      toast.success(t('common.saved'));
      setCurrentPassword('');
      setNewPassword('');
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : t('error.generic'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card id="account">
      <CardHeader>
        <div>
          <CardTitle>{t('auth.changePassword')}</CardTitle>
          <CardDescription>{t('auth.changePasswordHint')}</CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="current">{t('auth.currentPassword')}</Label>
            <Input
              id="current"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="next">{t('auth.newPassword')}</Label>
            <Input
              id="next"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </div>

          {error && (
            <p role="alert" className="text-destructive text-[13px] sm:col-span-2">
              {error}
            </p>
          )}

          <div className="sm:col-span-2">
            <Button type="submit" loading={saving} disabled={!currentPassword || !newPassword}>
              {t('action.save')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * Το ειδοποιητήριο by email — the agency's template and its dispatch policy.
 *
 * The screen is arranged as the decision is actually made: first whether notices go out at all,
 * then whether the system sends them or waits to be told, then — only if it sends on its own —
 * how far ahead, and finally the words. A mode that changes what the fields below it mean should
 * come before them, not be buried under the template.
 */
function RenewalEmailCard() {
  const { t, dateTime } = useI18n();
  const { data: settings, isLoading } = useRenewalEmailSettings();
  const save = useSaveRenewalEmailSettings();
  const sendTest = useSendTestRenewalEmail();

  const [form, setForm] = React.useState<{
    enabled: boolean;
    mode: RenewalEmailMode;
    leadDays: string;
    subject: string;
    body: string;
    senderName: string;
    replyTo: string;
  } | null>(null);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [testTo, setTestTo] = React.useState('');
  const bodyRef = React.useRef<HTMLTextAreaElement>(null);

  // Seeded once the settings land, then owned by the form — a later refetch must not overwrite
  // half-typed copy under the cursor.
  React.useEffect(() => {
    if (!settings || form) return;
    setForm({
      enabled: settings.enabled,
      mode: settings.mode,
      leadDays: String(settings.leadDays),
      subject: settings.subject,
      body: settings.body,
      senderName: settings.senderName ?? '',
      replyTo: settings.replyTo ?? '',
    });
  }, [settings, form]);

  const set = <K extends keyof NonNullable<typeof form>>(key: K, value: NonNullable<typeof form>[K]) =>
    setForm((current) => (current ? { ...current, [key]: value } : current));

  /**
   * Inserts a placeholder at the cursor rather than appending it.
   *
   * Appending is the easy version and the wrong one: the field a writer wants is almost always
   * needed mid-sentence, and a chip that drops «{{premium}}» after the signature costs a
   * cut-and-paste every time it is used.
   */
  const insertPlaceholder = (name: string) => {
    const textarea = bodyRef.current;
    const token = `{{${name}}}`;
    if (!textarea || !form) {
      set('body', `${form?.body ?? ''}${token}`);
      return;
    }

    const start = textarea.selectionStart ?? form.body.length;
    const end = textarea.selectionEnd ?? start;
    const next = form.body.slice(0, start) + token + form.body.slice(end);
    set('body', next);

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form) return;
    setErrors({});

    const parsed = renewalEmailSettingsSchema.safeParse({
      ...form,
      leadDays: form.leadDays,
      senderName: form.senderName || null,
      replyTo: form.replyTo || null,
    });

    if (!parsed.success) {
      setErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [String(issue.path[0] ?? 'form'), issue.message]),
        ),
      );
      return;
    }

    try {
      await save.mutateAsync(parsed.data);
      toast.success(t('common.saved'));
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        // The server's field errors — an unknown {{placeholder}} above all — land on the input
        // that produced them rather than in a toast that disappears before it can be acted on.
        const fields = Object.fromEntries(
          (caught.errors ?? [])
            .filter((error) => error.field)
            .map((error) => [error.field!, error.message]),
        );
        setErrors(Object.keys(fields).length ? fields : { form: caught.message });
      } else {
        setErrors({ form: t('error.generic') });
      }
    }
  };

  const runTest = async () => {
    try {
      const result = await sendTest.mutateAsync(testTo.trim());
      if (result.sent) toast.success(t('settings.renewalEmailTestSent', { email: testTo.trim() }));
      else toast.error(result.reason ?? t('error.generic'));
    } catch (caught) {
      toast.error(caught instanceof ApiRequestError ? caught.message : t('error.generic'));
    }
  };

  return (
    <Card id="renewal-email">
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            <Mail className="text-muted-soft size-4" />
            {t('settings.renewalEmail')}
          </CardTitle>
          <CardDescription>{t('settings.renewalEmailHint')}</CardDescription>
        </div>
      </CardHeader>

      <CardContent>
        {isLoading || !form ? (
          <div className="space-y-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-2/3" />
            <Skeleton className="h-28 w-full" />
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            {/* Stated before anything is typed, not after a save that appeared to work. */}
            {!settings?.mailConfigured && (
              <p className="border-warning/40 bg-warning/[0.07] text-muted-foreground flex items-start gap-2 rounded-lg border px-3 py-2 text-[13px]">
                <TriangleAlert className="text-warning mt-0.5 size-4 shrink-0" />
                {t('settings.renewalEmailNoSmtp')}
              </p>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <Label htmlFor="renewalEmailEnabled" className="text-sm">
                  {t('settings.renewalEmailEnabled')}
                </Label>
                <p className="text-muted-soft mt-0.5 text-[12.5px]">
                  {t('settings.renewalEmailEnabledHint')}
                </p>
              </div>
              <Switch
                id="renewalEmailEnabled"
                checked={form.enabled}
                onCheckedChange={(checked) => set('enabled', Boolean(checked))}
              />
            </div>

            <Separator />

            <div className="space-y-2">
              <OptionRow label={t('settings.renewalEmailMode')}>
                {(
                  [
                    { value: 'manual', label: t('settings.renewalEmailModeManual') },
                    { value: 'auto', label: t('settings.renewalEmailModeAuto') },
                  ] as const
                ).map((option) => (
                  <SegmentButton
                    key={option.value}
                    active={form.mode === option.value}
                    onClick={() => set('mode', option.value)}
                  >
                    {option.label}
                  </SegmentButton>
                ))}
              </OptionRow>
              <p className="text-muted-soft text-[12.5px]">
                {form.mode === 'auto'
                  ? t('settings.renewalEmailModeAutoHint')
                  : t('settings.renewalEmailModeManualHint')}
              </p>
            </div>

            {/*
              Shown in both modes, because the manual "send everything due" button uses the same
              window — hiding it under `manual` would make that button's scope unknowable.
            */}
            <div className="space-y-1.5">
              <Label htmlFor="renewalEmailLeadDays">{t('settings.renewalEmailLeadDays')}</Label>
              <Input
                id="renewalEmailLeadDays"
                type="number"
                min={1}
                max={120}
                value={form.leadDays}
                onChange={(event) => set('leadDays', event.target.value)}
                className="w-28 tabular"
                aria-invalid={Boolean(errors.leadDays)}
              />
              {/* The field means something in both modes, and something different in each — left
                  unsaid it reads as dead configuration while «Χειροκίνητα» is selected. */}
              <p className="text-muted-soft text-[12.5px]">
                {form.mode === 'auto'
                  ? t('settings.renewalEmailLeadDaysAuto')
                  : t('settings.renewalEmailLeadDaysManual')}
              </p>
              <FieldError message={errors.leadDays} />
            </div>

            <Separator />

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="renewalEmailSender">{t('settings.renewalEmailSender')}</Label>
                <Input
                  id="renewalEmailSender"
                  value={form.senderName}
                  onChange={(event) => set('senderName', event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="renewalEmailReplyTo">{t('settings.renewalEmailReplyTo')}</Label>
                <Input
                  id="renewalEmailReplyTo"
                  type="email"
                  value={form.replyTo}
                  onChange={(event) => set('replyTo', event.target.value)}
                  aria-invalid={Boolean(errors.replyTo)}
                />
                <FieldError message={errors.replyTo} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="renewalEmailSubject">{t('settings.renewalEmailSubject')}</Label>
              <Input
                id="renewalEmailSubject"
                value={form.subject}
                onChange={(event) => set('subject', event.target.value)}
                aria-invalid={Boolean(errors.subject)}
              />
              <FieldError message={errors.subject} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="renewalEmailBody">{t('settings.renewalEmailBody')}</Label>
              <Textarea
                id="renewalEmailBody"
                ref={bodyRef}
                rows={12}
                value={form.body}
                onChange={(event) => set('body', event.target.value)}
                aria-invalid={Boolean(errors.body)}
                className="font-normal"
              />
              <FieldError message={errors.body} />
            </div>

            <div className="space-y-2">
              <div>
                <Label className="text-sm">{t('settings.renewalEmailPlaceholders')}</Label>
                <p className="text-muted-soft mt-0.5 text-[12.5px]">
                  {t('settings.renewalEmailPlaceholdersHint')}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(settings?.placeholders ?? []).map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => insertPlaceholder(name)}
                    className="border-border hover:bg-accent hover:text-foreground text-muted-foreground tabular rounded-md border px-2 py-1 text-[12px] transition-colors"
                  >
                    {`{{${name}}}`}
                  </button>
                ))}
              </div>
            </div>

            {errors.form && (
              <p role="alert" className="text-destructive text-[13px]">
                {errors.form}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <RequiresConnection reasonKey="offline.blocked.contract">
                <Button type="submit" loading={save.isPending}>
                  {t('action.save')}
                </Button>
              </RequiresConnection>
              {settings?.updatedByName && (
                <span className="text-muted-soft text-[12.5px]">
                  {t('settings.renewalEmailUpdatedBy', {
                    name: settings.updatedByName,
                    date: dateTime(settings.updatedAt),
                  })}
                </span>
              )}
            </div>

            <Separator />

            {/*
              The test send is its own row rather than a field in the form above: it sends
              whatever is *saved*, not what is typed, and pairing it with the save button would
              invite the opposite assumption.
            */}
            <div className="space-y-1.5">
              <Label htmlFor="renewalEmailTest">{t('settings.renewalEmailTest')}</Label>
              <p className="text-muted-soft text-[12.5px]">{t('settings.renewalEmailTestHint')}</p>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  id="renewalEmailTest"
                  type="email"
                  value={testTo}
                  onChange={(event) => setTestTo(event.target.value)}
                  placeholder="name@example.gr"
                  className="max-w-xs"
                />
                <Button
                  type="button"
                  variant="outline"
                  loading={sendTest.isPending}
                  disabled={!testTo.includes('@')}
                  onClick={() => void runTest()}
                >
                  <Send /> {t('notices.emailSendShort')}
                </Button>
              </div>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p role="alert" className="text-destructive text-[12.5px]">
      {message}
    </p>
  );
}
